import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTier } from "@/contexts/TierContext";

export type Tier = "starter" | "beta_free" | "pro" | "elite";

export type Profile = {
  id: string;
  tier: Tier;
  beta_started_at?: string | null;
  beta_expires_at?: string | null;
};

export type FeatureKey = "calculator" | "plan" | "journal" | "portfolio" | "progress" | "screenshots";

export type AccessDenyReason = "login_required" | "upgrade_required" | "tier_loading";

export interface FeatureAccessResult {
  allowed: boolean;
  mode?: "full" | "read_only";
  reason?: AccessDenyReason;
  requiredTier?: Tier;
}

export interface TierLimits {
  maxAccounts: number | null;
  maxActiveGoals: number | null;
  maxActiveChallenge: number | null;
}

export interface LimitCheckResult {
  allowed: boolean;
  reason?: "limit_reached";
  limit?: number;
  current?: number;
  requiredTier?: Tier;
}

interface AccessContextValue {
  tier: Tier;
  accessTier: Tier;
  loadingTier: boolean;
  isLoggedIn: boolean;
  hasFullAccess: boolean;
  betaActive: boolean;
  betaExpired: boolean;
  betaDaysLeft: number | null;
  checkFeature: (
    feature: FeatureKey,
    opts?: {
      allowReadOnlyIfDowngraded?: boolean;
      hasExistingData?: boolean;
    }
  ) => FeatureAccessResult;
  getLimits: () => TierLimits;
  checkCanCreateAccount: (currentAccounts: number) => LimitCheckResult;
  checkCanCreateActiveGoal: (currentActiveGoals: number) => LimitCheckResult;
}

const AccessContext = createContext<AccessContextValue | undefined>(undefined);

const TIER_RANK: Record<Tier, number> = {
  starter: 0,
  beta_free: 0,
  pro: 1,
  elite: 2,
};
function tierGte(current: Tier, required: Tier): boolean {
  return TIER_RANK[current] >= TIER_RANK[required];
}

function requiredTierForFeature(feature: FeatureKey): Tier {
  if (feature === "calculator") return "starter";
  if (feature === "plan") return "pro";
  if (feature === "journal") return "pro";
  if (feature === "screenshots") return "pro";
  if (feature === "portfolio") return "pro";
  if (feature === "progress") return "pro";
  return "starter";
}

function limitsForTier(tier: Tier): TierLimits {
  if (tier === "starter") {
    return { maxAccounts: 1, maxActiveGoals: 0, maxActiveChallenge: 0 };
  }
  if (tier === "pro") {
    return { maxAccounts: 5, maxActiveGoals: 5, maxActiveChallenge: null };
  }
  return { maxAccounts: null, maxActiveGoals: null, maxActiveChallenge: null };
}

function requiredTierForAccountCount(nextCount: number): Tier {
  if (nextCount <= 5) return "pro";
  return "elite";
}

function requiredTierForActiveGoalsCount(nextCount: number): Tier {
  if (nextCount <= 5) return "pro";
  return "elite";
}

export function getBetaDaysLeft(beta_expires_at?: string | null): number | null {
  if (!beta_expires_at) return null;
  const msLeft = new Date(beta_expires_at).getTime() - Date.now();
  const days = Math.ceil(msLeft / 86400000);
  return days;
}

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tier, loadingTier, beta_expires_at } = useTier();

  const isLoggedIn = Boolean(user);

  const betaState = useMemo(() => {
    const nowMs = Date.now();

    const betaActive =
      tier === "beta_free" &&
      !!beta_expires_at &&
      new Date(beta_expires_at).getTime() > nowMs;

    const betaExpired =
      tier === "beta_free" &&
      !!beta_expires_at &&
      new Date(beta_expires_at).getTime() <= nowMs;

    const hasFullAccess = tier === "pro" || tier === "elite" || betaActive;

    const betaDaysLeft = betaActive ? getBetaDaysLeft(beta_expires_at) : null;

    return { betaActive, betaExpired, hasFullAccess, betaDaysLeft };
  }, [beta_expires_at, tier]);

  const accessTier = useMemo<Tier>(() => {
    if (tier !== "beta_free") return tier;
    if (betaState.betaActive) return "elite";
    return "starter";
  }, [betaState.betaActive, tier]);
  const value = useMemo<AccessContextValue>(() => {
    const checkFeature: AccessContextValue["checkFeature"] = (feature, opts) => {
      const requiredTier = requiredTierForFeature(feature);
      const allowReadOnlyIfDowngraded = opts?.allowReadOnlyIfDowngraded === true;
      const hasExistingData = opts?.hasExistingData === true;

      if (feature !== "calculator" && !isLoggedIn) {
        return { allowed: false, reason: "login_required", requiredTier };
      }

      if (feature !== "calculator" && isLoggedIn && loadingTier) {
        return { allowed: false, reason: "tier_loading", requiredTier };
      }

      if (!tierGte(accessTier, requiredTier)) {
        if (allowReadOnlyIfDowngraded && hasExistingData) {
          return { allowed: true, mode: "read_only", reason: "upgrade_required", requiredTier };
        }
        return { allowed: false, reason: "upgrade_required", requiredTier };
      }

      return { allowed: true, mode: "full" };
    };

    const getLimits = (): TierLimits => limitsForTier(accessTier);

    const checkCanCreateAccount = (currentAccounts: number): LimitCheckResult => {
      const { maxAccounts } = limitsForTier(accessTier);
      if (maxAccounts === null) return { allowed: true };
      const nextCount = currentAccounts + 1;
      if (nextCount <= maxAccounts) return { allowed: true };
      return {
        allowed: false,
        reason: "limit_reached",
        limit: maxAccounts,
        current: currentAccounts,
        requiredTier: requiredTierForAccountCount(nextCount),
      };
    };

    const checkCanCreateActiveGoal = (currentActiveGoals: number): LimitCheckResult => {
      const { maxActiveGoals } = limitsForTier(accessTier);
      if (maxActiveGoals === null) return { allowed: true };
      const nextCount = currentActiveGoals + 1;
      if (nextCount <= maxActiveGoals) return { allowed: true };
      return {
        allowed: false,
        reason: "limit_reached",
        limit: maxActiveGoals,
        current: currentActiveGoals,
        requiredTier: requiredTierForActiveGoalsCount(nextCount),
      };
    };

    return {
      tier,
      accessTier,
      loadingTier,
      isLoggedIn,
      hasFullAccess: betaState.hasFullAccess,
      betaActive: betaState.betaActive,
      betaExpired: betaState.betaExpired,
      betaDaysLeft: betaState.betaDaysLeft,
      checkFeature,
      getLimits,
      checkCanCreateAccount,
      checkCanCreateActiveGoal,
    };
  }, [accessTier, betaState, isLoggedIn, loadingTier, tier]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export function useAccess(): AccessContextValue {
  const ctx = useContext(AccessContext);
  if (!ctx) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return ctx;
}
